import { useCallback } from 'react'
import html2canvas from 'html2canvas'
import { jsPDF } from 'jspdf'
import JSZip from 'jszip'

export const useExport = () => {
  // Helper function to get page content as HTML element
  const getPageAsElement = (content, pageNumber) => {
    const tempDiv = document.createElement('div')
    tempDiv.style.width = '620px'
    tempDiv.style.padding = '40px'
    tempDiv.style.background = 'white'
    tempDiv.style.position = 'absolute'
    tempDiv.style.left = '-9999px'
    tempDiv.style.top = '0'
    tempDiv.style.fontFamily = 'Inter, sans-serif'
    tempDiv.style.fontSize = '14px'
    tempDiv.style.lineHeight = '1.6'
    tempDiv.innerHTML = content
    tempDiv.id = `export-page-${pageNumber}`
    
    return tempDiv
  }

  const exportToPNG = useCallback(async (element, fileName = 'resume.png') => {
    try {
      // Create a clone to avoid affecting the original element
      const clone = element.cloneNode(true);
      clone.style.position = 'absolute';
      clone.style.left = '-9999px';
      clone.style.top = '0';
      document.body.appendChild(clone);
      
      const canvas = await html2canvas(clone, {
        scale: 3,
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false,
        width: clone.offsetWidth,
        height: clone.offsetHeight,
        windowWidth: clone.scrollWidth,
        windowHeight: clone.scrollHeight
      })
      
      // Remove clone
      document.body.removeChild(clone);
      
      const link = document.createElement('a')
      link.download = fileName
      link.href = canvas.toDataURL('image/png', 1.0)
      link.click()
      
      return true
    } catch (error) {
      console.error('PNG export failed:', error)
      throw new Error(`Failed to export PNG: ${error.message}`)
    }
  }, [])

  const exportToJPG = useCallback(async (element, fileName = 'resume.jpg') => {
  try {
    // Create a clone to avoid affecting the original element
    const clone = element.cloneNode(true);
    clone.style.position = 'absolute';
    clone.style.left = '-9999px';
    clone.style.top = '0';
    clone.style.visibility = 'visible';
    clone.style.opacity = '1';
    document.body.appendChild(clone);
    
    // Wait for clone to be rendered
    await new Promise(resolve => setTimeout(resolve, 100));
    
    const canvas = await html2canvas(clone, {
      scale: 3,
      useCORS: true,
      backgroundColor: '#ffffff',
      logging: false,
      width: clone.offsetWidth,
      height: clone.offsetHeight,
      windowWidth: clone.scrollWidth,
      windowHeight: clone.scrollHeight,
      onclone: (clonedDoc) => {
        // Ensure all styles are properly applied to clone
        const clonedElement = clonedDoc.querySelector('div');
        if (clonedElement) {
          clonedElement.style.visibility = 'visible';
          clonedElement.style.opacity = '1';
        }
      }
    })
    
    // Remove clone
    document.body.removeChild(clone);
    
    const link = document.createElement('a');
    link.download = fileName;
    link.href = canvas.toDataURL('image/jpeg', 0.95);
    
    // Append link, click, and remove
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    return true;
  } catch (error) {
    console.error('JPG export failed:', error);
    throw new Error(`Failed to export JPG: ${error.message}`);
  }
}, []);

  const exportToPDF = useCallback(async (element, fileName = 'resume.pdf') => {
    try {
      // Create a clone to avoid affecting the original element
      const clone = element.cloneNode(true);
      clone.style.position = 'absolute';
      clone.style.left = '-9999px';
      clone.style.top = '0';
      document.body.appendChild(clone);
      
      // Calculate dimensions for PDF
      const width = clone.offsetWidth
      const height = clone.offsetHeight
      
      // Create PDF with proper dimensions (in mm)
      const pdfWidth = 210 // A4 width in mm
      const pdfHeight = (height / width) * pdfWidth
      
      const pdf = new jsPDF({
        orientation: pdfHeight > 297 ? 'portrait' : 'portrait',
        unit: 'mm',
        format: [pdfWidth, pdfHeight || 297]
      })
      
      const canvas = await html2canvas(clone, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false,
        width: width,
        height: height,
        windowWidth: clone.scrollWidth,
        windowHeight: clone.scrollHeight
      })
      
      // Remove clone
      document.body.removeChild(clone);
      
      const imgData = canvas.toDataURL('image/jpeg', 0.9)
      
      // Calculate image dimensions to fit PDF page
      const imgWidth = pdfWidth
      const imgHeight = (canvas.height * pdfWidth) / canvas.width
      
      pdf.addImage(imgData, 'JPEG', 0, 0, imgWidth, imgHeight)
      pdf.save(fileName)
      
      return true
    } catch (error) {
      console.error('PDF export failed:', error)
      throw new Error(`Failed to export PDF: ${error.message}`)
    }
  }, [])

  // Export multiple pages as PDF
  const exportMultiplePagesAsPDF = useCallback(async (pages, fileName = 'resume.pdf') => {
    try {
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      })
      
      // Remove existing temporary elements
      document.querySelectorAll('[id^="export-page-"]').forEach(el => el.remove())
      
      for (let i = 0; i < pages.length; i++) {
        const pageElement = getPageAsElement(pages[i], i + 1)
        document.body.appendChild(pageElement)
        
        try {
          const canvas = await html2canvas(pageElement, {
            scale: 2,
            useCORS: true,
            backgroundColor: '#ffffff',
            logging: false,
            width: pageElement.offsetWidth,
            height: pageElement.offsetHeight
          })
          
          const imgData = canvas.toDataURL('image/jpeg', 0.9)
          const imgWidth = 190 // A4 width minus margins
          const imgHeight = (canvas.height * imgWidth) / canvas.width
          
          if (i > 0) {
            pdf.addPage()
          }
          
          // Center the image on page
          const xPos = (210 - imgWidth) / 2
          const yPos = (297 - imgHeight) / 2
          
          pdf.addImage(imgData, 'JPEG', xPos, yPos, imgWidth, imgHeight)
          
        } finally {
          document.body.removeChild(pageElement)
        }
      }
      
      pdf.save(fileName)
      return true
    } catch (error) {
      console.error('Multi-page PDF export failed:', error)
      throw new Error(`Failed to export multi-page PDF: ${error.message}`)
    }
  }, [])

  // Export multiple pages as images (PNG/JPEG) in ZIP
  const exportMultiplePagesAsImages = useCallback(async (pages, format = 'png', fileName = 'resume.zip') => {
    try {
      const zip = new JSZip()
      const imagePromises = []
      
      // Remove existing temporary elements
      document.querySelectorAll('[id^="export-page-"]').forEach(el => el.remove())
      
      // Create canvases for each page
      for (let i = 0; i < pages.length; i++) {
        const pageElement = getPageAsElement(pages[i], i + 1)
        document.body.appendChild(pageElement)
        
        const canvasPromise = html2canvas(pageElement, {
          scale: 3,
          useCORS: true,
          backgroundColor: '#ffffff',
          logging: false,
          width: pageElement.offsetWidth,
          height: pageElement.offsetHeight
        }).then(canvas => {
          const imageData = format === 'png' 
            ? canvas.toDataURL('image/png', 1.0)
            : canvas.toDataURL('image/jpeg', 0.95)
          
          // Remove data URL prefix
          const base64Data = imageData.split(',')[1]
          
          // Add to zip
          const pageName = `page-${i + 1}.${format}`
          zip.file(pageName, base64Data, { base64: true })
          
          return pageName
        }).catch(error => {
          console.error(`Failed to export page ${i + 1}:`, error)
          throw error
        }).finally(() => {
          document.body.removeChild(pageElement)
        })
        
        imagePromises.push(canvasPromise)
      }
      
      // Wait for all canvases to be created
      await Promise.all(imagePromises)
      
      // Generate ZIP file
      const zipBlob = await zip.generateAsync({ type: 'blob' })
      
      // Download ZIP
      const link = document.createElement('a')
      link.href = URL.createObjectURL(zipBlob)
      link.download = fileName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      
      // Cleanup
      setTimeout(() => URL.revokeObjectURL(link.href), 1000)
      
      return true
    } catch (error) {
      console.error('Multi-page image export failed:', error)
      throw new Error(`Failed to export multi-page images: ${error.message}`)
    }
  }, [])

  return {
    exportToPNG,
    exportToJPG,
    exportToPDF,
    exportMultiplePagesAsPDF,
    exportMultiplePagesAsImages
  }
}